
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

// Utils
import { formatLocalDateTime } from "utils/dateFormatter";

interface PointUseRequestDetailModalProps {
    open: boolean;
    onClose: () => void;
    requestData: any;
}

const PointUseRequestDetailModal: React.FC<PointUseRequestDetailModalProps> = ({
    open,
    onClose,
    requestData,
}) => {
    if (!requestData) return null;

    const renderStatusBadge = (status: string) => {
        let badgeColor: "warning" | "success" | "error" | "secondary" | "info" = "warning";
        let badgeText = "대기";

        if (status === "APPROVED" || status === "POINT_REQ_APPROVED") {
            badgeColor = "success";
            badgeText = "승인됨";
        } else if (status === "SENT") {
            badgeColor = "success";
            badgeText = "승인됨";
        } else if (status === "REJECTED" || status === "POINT_REQ_REJECTED") {
            badgeColor = "error";
            badgeText = "거부";
        } else if (status === "CANCELED" || status === "POINT_REQ_CANCELED" || status === "CANCELLED") {
            badgeColor = "secondary";
            badgeText = "취소됨";
        } else if (status === "USED") {
            badgeColor = "info";
            badgeText = "사용됨";
        }

        return (
            <MDBadge
                badgeContent={badgeText}
                color={badgeColor}
                variant="gradient"
                size="md"
            />
        );
    };

    const SectionHeader = ({ icon, title }: { icon: string; title: string }) => (
        <MDBox display="flex" alignItems="center" mb={1.5}>
            <MDBox
                variant="gradient"
                bgColor="info"
                color="white"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="2rem"
                height="2rem"
                mr={1.5}
            >
                <Icon fontSize="small">{icon}</Icon>
            </MDBox>
            <MDTypography variant="h6" fontWeight="medium">
                {title}
            </MDTypography>
        </MDBox>
    );

    const InfoItem = ({ label, value, isBold = false, isPrice = false }: { label: string; value: string; isBold?: boolean; isPrice?: boolean }) => (
        <MDBox display="flex" justifyContent="space-between" mb={1} py={0.5} borderBottom="1px solid" borderColor="divider">
            <MDTypography variant="button" color="text" fontWeight="regular">
                {label}
            </MDTypography>
            <MDTypography variant="button" fontWeight={isBold ? "bold" : "medium"} color={isPrice ? "warning" : "text"}>
                {value}
            </MDTypography>
        </MDBox>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    bgcolor: "background.paper"
                },
            }}
        >
            <DialogTitle sx={{ p: 3 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon fontSize="medium">receipt_long</Icon>
                        <MDTypography variant="h5" fontWeight="bold">
                            포인트 사용 요청 상세
                        </MDTypography>
                    </MDBox>
                    {renderStatusBadge(requestData.requestStatus)}
                </MDBox>
            </DialogTitle>
            <Divider sx={{ my: 0 }} />
            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* 사용자 정보 & 요청 정보 Row */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%", boxShadow: "sm" }}>
                            <MDBox p={2}>
                                <SectionHeader icon="person" title="사용자 정보" />
                                <MDBox mt={2}>
                                    <InfoItem label="사용자명" value={requestData.userName} />
                                    <InfoItem label="사용자 연락처" value={requestData.userPhone || "-"} />
                                    {requestData.senderPhone && (
                                        <InfoItem label="발신번호" value={requestData.senderPhone} />
                                    )}
                                    {requestData.receiverPhone && (
                                        <InfoItem label="수신번호" value={requestData.receiverPhone} />
                                    )}
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%", boxShadow: "sm" }}>
                            <MDBox p={2}>
                                <SectionHeader icon="info" title="요청 정보" />
                                <MDBox mt={2}>
                                    <InfoItem
                                        label="사용 유형"
                                        value={
                                            requestData.useType === "POINT_USE_LOCAL_VOUCHER" ? "지역화폐" :
                                                requestData.useType === "POINT_USE_GIFTISHOW" ? "기프티쇼" : requestData.useType
                                        }
                                    />
                                    <InfoItem
                                        label="요청 포인트"
                                        value={`${requestData.requestPoint} P`}
                                        isBold
                                        isPrice
                                    />
                                    <InfoItem label="요청 일시" value={formatLocalDateTime(requestData.crtDt)} />
                                    <MDBox mt={2} p={1.5} bgcolor="#f8f9fa" borderRadius="md" border="1px solid #eee">
                                        <MDTypography variant="caption" color="text" display="block" mb={0.5}>메시지</MDTypography>
                                        <MDTypography variant="body2">{requestData.requestMessage || "-"}</MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>

                    {/* 기프티쇼 상품 정보 (Optional) */}
                    {requestData.giftishowProduct && (
                        <Grid item xs={12}>
                            <Card sx={{ boxShadow: "sm" }}>
                                <MDBox p={2}>
                                    <SectionHeader icon="card_giftcard" title="상품 정보" />
                                    <MDBox display="flex" gap={3} mt={1} alignItems="center">
                                        <MDBox
                                            component="img"
                                            src={requestData.giftishowProduct.goodsImgB || requestData.giftishowProduct.goodsImgS}
                                            alt={requestData.giftishowProduct.goodsName}
                                            width="100px"
                                            height="100px"
                                            borderRadius="lg"
                                            sx={{ objectFit: "contain", border: "1px solid #f0f0f0", p: 1, bgcolor: "#fff" }}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={0.5} flex={1}>
                                            <MDTypography variant="caption" color="text" fontWeight="medium">
                                                {requestData.giftishowProduct.brandName}
                                            </MDTypography>
                                            <MDTypography variant="h6" fontWeight="bold">
                                                {requestData.giftishowProduct.goodsName}
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text">
                                                상품코드: {requestData.giftishowProduct.goodsCode}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                            </Card>
                        </Grid>
                    )}

                    {/* 처리 정보 */}
                    {(requestData.processedDt || requestData.processedByName) && (
                        <Grid item xs={12}>
                            <Card sx={{ boxShadow: "sm" }}>
                                <MDBox p={2}>
                                    <SectionHeader
                                        icon="history"
                                        title={["CANCELED", "POINT_REQ_CANCELED", "CANCELLED"].includes(requestData.requestStatus) ? "처리 및 취소 내역" : "처리 내역"}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <InfoItem
                                                label="처리자"
                                                value={requestData.processedByName || "-"}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <InfoItem
                                                label="처리 일시"
                                                value={formatLocalDateTime(requestData.processedDt)}
                                            />
                                        </Grid>

                                        {["CANCELED", "POINT_REQ_CANCELED", "CANCELLED"].includes(requestData.requestStatus) && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <InfoItem
                                                        label="취소자"
                                                        value={(requestData as any).cancelerName || "-"}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <InfoItem
                                                        label="취소 일시"
                                                        value={formatLocalDateTime((requestData as any).cancelDt)}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                    {requestData.adminMemo && requestData.adminMemo !== "-" && (
                                        <MDBox mt={2}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text">관리자 메모</MDTypography>
                                            <MDBox mt={0.5} p={1.5} bgcolor="#f1f3f5" borderRadius="md">
                                                <MDTypography variant="body2">{requestData.adminMemo}</MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    )}

                                    {requestData.rejectionReason && requestData.rejectionReason !== "-" && (
                                        <MDBox mt={2}>
                                            <MDTypography variant="caption" fontWeight="bold" color="error">거절 사유</MDTypography>
                                            <MDBox mt={0.5} p={1.5} bgcolor="#fff5f5" borderRadius="md" border="1px solid #ffcccc">
                                                <MDTypography variant="body2" color="error">{requestData.rejectionReason}</MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <Divider sx={{ my: 0 }} />
            <DialogActions sx={{ p: 2 }}>
                <MDButton onClick={onClose} variant="outlined" color="dark">
                    닫기
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default PointUseRequestDetailModal;
